/**
 * Weekly working-hours editor, shared by the tenant defaults and the per-vehicle
 * override.
 *
 * The form asks for WORKING hours, not private ones, and privacy is their
 * complement. That is not a wording choice: with private windows, "private all
 * weekend" left Monday 00:00-08:00 tracked, because that stretch belonged to
 * Sunday's window and a full Sunday does not spill into Monday. Asking when
 * people work makes such a gap impossible to express.
 *
 * Each day is one of three states, which is what lets "private all weekend" be
 * said at all:
 *   - Travaillé  : hours entered
 *   - Non travaillé : private around the clock
 *   - 24h/24     : never private
 */

import { Copy } from 'lucide-react';
import clsx from 'clsx';

export type WorkDay = { allDay: true } | { start: string; end: string } | null;
export type WorkSchedule = Record<string, WorkDay>;

const DAYS: { key: string; label: string; short: string }[] = [
  { key: '1', label: 'Lundi', short: 'Lun' },
  { key: '2', label: 'Mardi', short: 'Mar' },
  { key: '3', label: 'Mercredi', short: 'Mer' },
  { key: '4', label: 'Jeudi', short: 'Jeu' },
  { key: '5', label: 'Vendredi', short: 'Ven' },
  { key: '6', label: 'Samedi', short: 'Sam' },
  { key: '7', label: 'Dimanche', short: 'Dim' },
];

/** Mon-Fri 08:00-18:00, weekend off — the shape most fleets start from. */
export const DEFAULT_WORK_SCHEDULE: WorkSchedule = {
  '1': { start: '08:00', end: '18:00' },
  '2': { start: '08:00', end: '18:00' },
  '3': { start: '08:00', end: '18:00' },
  '4': { start: '08:00', end: '18:00' },
  '5': { start: '08:00', end: '18:00' },
  '6': null,
  '7': null,
};

type DayState = 'worked' | 'off' | 'always';

const stateOf = (day: WorkDay): DayState => {
  if (!day) return 'off';
  if ('allDay' in day) return 'always';
  return 'worked';
};

/** At least one day must be worked, or the vehicle would never be tracked. */
export function hasSomeWork(schedule: WorkSchedule): boolean {
  return Object.values(schedule).some((day) => day !== null && day !== undefined);
}

/** Plain-French summary of what the schedule means for privacy. */
export function privacySummary(schedule: WorkSchedule): string {
  const off = DAYS.filter((d) => stateOf(schedule[d.key]) === 'off');
  const worked = DAYS.filter((d) => stateOf(schedule[d.key]) === 'worked');

  if (worked.length === 0 && off.length === 7) return 'Privé en permanence';

  const parts: string[] = [];
  if (worked.length > 0) {
    const first = schedule[worked[0].key] as { start: string; end: string };
    const uniform = worked.every((d) => {
      const h = schedule[d.key] as { start: string; end: string };
      return h.start === first.start && h.end === first.end;
    });
    parts.push(
      uniform
        ? `privé hors ${first.start}-${first.end} (${worked.map((d) => d.short).join(', ')})`
        : `privé hors horaires (${worked.map((d) => d.short).join(', ')})`
    );
  }
  if (off.length > 0) {
    parts.push(`privé toute la journée ${off.map((d) => d.short).join(', ')}`);
  }
  return parts.join(' · ');
}

export default function WorkScheduleEditor({
  value,
  onChange,
  disabled,
}: {
  value: WorkSchedule;
  onChange: (next: WorkSchedule) => void;
  disabled?: boolean;
}) {
  const setDay = (key: string, day: WorkDay) => onChange({ ...value, [key]: day });

  const setState = (key: string, next: DayState) => {
    if (next === 'off') return setDay(key, null);
    if (next === 'always') return setDay(key, { allDay: true });
    const current = value[key];
    const hours =
      current && !('allDay' in current) ? current : { start: '08:00', end: '18:00' };
    setDay(key, hours);
  };

  /** Copy Monday's hours to every other worked day — the common case. */
  const copyMonday = () => {
    const monday = value['1'];
    if (!monday || 'allDay' in monday) return;
    const next: WorkSchedule = { ...value };
    for (const d of DAYS.slice(1)) {
      if (stateOf(value[d.key]) === 'worked') next[d.key] = { ...monday };
    }
    onChange(next);
  };

  const mondayWorked = stateOf(value['1']) === 'worked';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Heures de travail</span>
        {mondayWorked && !disabled && (
          <button
            type="button"
            onClick={copyMonday}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
          >
            <Copy className="h-3 w-3" />
            Copier lundi sur les autres jours
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        {DAYS.map((day) => {
          const state = stateOf(value[day.key]);
          const hours = value[day.key];

          return (
            <div
              key={day.key}
              className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-gray-200 px-3 py-2"
            >
              {/* The day name takes the full first line on a phone, so the
                  select and the two time inputs get a line of their own instead
                  of being squeezed into slivers. */}
              <span className="w-full text-sm font-medium text-gray-700 sm:w-20 sm:flex-shrink-0 sm:font-normal">
                {day.label}
              </span>

              <select
                value={state}
                disabled={disabled}
                onChange={(e) => setState(day.key, e.target.value as DayState)}
                className="rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
              >
                <option value="worked">Travaillé</option>
                <option value="off">Non travaillé</option>
                <option value="always">Suivi 24h/24</option>
              </select>

              {state === 'worked' && hours && !('allDay' in hours) && (
                <div className="flex items-center gap-1.5">
                  <input
                    type="time"
                    value={hours.start}
                    disabled={disabled}
                    onChange={(e) => setDay(day.key, { ...hours, start: e.target.value })}
                    className="rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                  />
                  <span className="text-xs text-gray-400">→</span>
                  <input
                    type="time"
                    value={hours.end}
                    disabled={disabled}
                    onChange={(e) => setDay(day.key, { ...hours, end: e.target.value })}
                    className="rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                  />
                  {hours.start > hours.end && (
                    <span className="text-[11px] text-gray-500">équipe de nuit</span>
                  )}
                </div>
              )}

              <span
                className={clsx(
                  'text-[11px] sm:ml-auto',
                  state === 'off' ? 'text-indigo-600' : 'text-gray-400'
                )}
              >
                {state === 'off' && 'privé toute la journée'}
                {state === 'always' && 'jamais privé'}
                {state === 'worked' && 'privé en dehors'}
              </span>
            </div>
          );
        })}
      </div>

      {!hasSomeWork(value) && (
        <p className="text-xs text-red-600">
          Aucun jour travaillé : le véhicule ne serait jamais suivi. Renseignez au moins un jour.
        </p>
      )}
    </div>
  );
}
